import React, { useEffect, useMemo, useState } from 'react';
import "./EarlyCheckIn.css";
import Header from '../components/Header';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuth from "../../customHooks/useAuth.js"
import { toast } from 'react-toastify';
import PageLoader from '../components/PageLoader/PageLoader';
import axiosInstance from '../../axiosInstance/axiosInstance.js';
import { taxOn } from '../../common/gst';
import { formatMoney } from '../../common/money';
import PromoSection, { computeDiscount } from '../PostCheckInRequest/FoodSummary/PromoSection';
import { HOTEL_TIMEZONE } from "../../common/hotelTime";

const OPEN_REQUEST_STATUSES = [
    "Pending_Hotel",
    "Pending_Guest",
    "InProgress_Hotel",
    "Scheduled_Hotel",
];

const EARLY_CHECKIN_TYPE = "PreCheckin Early Checkin Request";

export default function EarlyCheckIn() {

    const location = useLocation();
    const navigate = useNavigate();

    const hotelId = location.state?.hotelId || "";
    const bookingId = location.state?.bookingId || "";

    // Need to Send to Hotel View
    const hotelName = location.state?.hotelName || "";
    const hotelAddress = location.state?.hotelAddress || "";
    const checkInDate = location.state?.checkInDate || "";
    const checkOutDate = location.state?.checkOutDate || "";
    const status = location.state?.status || "";
    const activeTab = location.state?.activeTab || "";
    const booking_code = location.state?.booking_code || "";

    // For Refresh Handle
    useAuth();

    const userId = localStorage.getItem("user_id");

    const [hourlyRate, setHourlyRate] = useState(null);
    const [maxHours, setMaxHours] = useState(null);
    const [gstPercentage, setGstPercentage] = useState(0);
    const [isLoadingConfig, setIsLoadingConfig] = useState(true);

    const [selectedRoomId, setSelectedRoomId] = useState("");
    const [hours, setHours] = useState(1);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [appliedPromo, setAppliedPromo] = useState(null);
    const handleApplyPromo = (offer) => setAppliedPromo(offer);
    const handleRemovePromo = () => setAppliedPromo(null);

    useEffect(() => {
        if (hotelId === "" || bookingId === "") {
            navigate("/bookings");
        }
    }, [hotelId, bookingId, navigate]);

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const response = await axiosInstance.get(`/api/v1/hotels/${hotelId}/early-checkin-info`);
                const rate = response?.data?.early_checkin_hourly_rate;
                const max = response?.data?.early_checkin_max_hours;
                if (rate === null || rate === undefined || max === null || max === undefined) {
                    toast.error("Your Hotel Has Not Configured Early Check-In Yet");
                    navigate("/bookings");
                    return;
                }
                setHourlyRate(rate);
                setMaxHours(max);
                setGstPercentage(response?.data?.early_checkin_gst_percentage ?? 0);
            } catch (error) {
                toast.error(error?.response?.data?.message || "Could Not Load Early Check-In Details");
                navigate("/bookings");
            } finally {
                setIsLoadingConfig(false);
            }
        };
        if (hotelId !== "") {
            fetchConfig();
        }
    }, [hotelId, navigate]);

    const hasOpenEarlyCheckin = (room) =>
        (room.requests || []).some(
            (request) => request.type === EARLY_CHECKIN_TYPE && OPEN_REQUEST_STATUSES.includes(request.status)
        );

    const isRoomSelectable = (room) =>
        !room.is_checkin_completed && !room.is_checkout_completed && !hasOpenEarlyCheckin(room);

    // Sorted by booked room id so the Room 1 / Room 2 numbering is the same
    // for every guest of the booking, whatever order the API returns.
    const sortedRooms = useMemo(
        () => [...(location.state?.bookedRooms || [])].sort((a, b) => String(a.id).localeCompare(String(b.id))),
        [location.state]
    );

    const roomLabel = (index) => `Room ${index + 1}`;

    const selectedRoom = sortedRooms.find((room) => room.id === selectedRoomId);

    const grossAmount = useMemo(
        () => (hourlyRate === null ? 0 : hours * hourlyRate),
        [hours, hourlyRate]
    );

    const discountAmount = useMemo(
        () => (appliedPromo ? computeDiscount(appliedPromo, grossAmount) : 0),
        [appliedPromo, grossAmount]
    );

    const taxAmount = taxOn(grossAmount - discountAmount, gstPercentage);
    const payableAmount = grossAmount - discountAmount + taxAmount;

    const newCheckInTime = useMemo(() => {
        if (!selectedRoom?.checkin_date) return "";
        const date = new Date(selectedRoom.checkin_date);
        date.setHours(date.getHours() - hours);
        return date.toLocaleString("en-IN", {
            timeZone: HOTEL_TIMEZONE,
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
        });
    }, [selectedRoom, hours]);

    const decreaseHours = () => setHours((current) => Math.max(1, current - 1));
    const increaseHours = () => setHours((current) => Math.min(maxHours || 1, current + 1));

    const handleRoomSelect = (room) => {
        if (!isRoomSelectable(room)) return;
        setSelectedRoomId(room.id);
        setErrors({ ...errors, roomId: "" });
    };

    const initials = (name) =>
        (name || "?")
            .trim()
            .split(/\s+/)
            .slice(0, 2)
            .map((part) => part[0])
            .join("")
            .toUpperCase();

    const blockedReason = (room) => {
        if (room.is_checkout_completed) return "Checked out";
        if (room.is_checkin_completed) return "Checked in";
        if (hasOpenEarlyCheckin(room)) return "Already requested";
        return "";
    };

    const handleSave = async () => {
        if (!selectedRoomId) {
            setErrors({ roomId: "Room selection is required" });
            return;
        }

        setIsSubmitting(true);
        try {
            const passData = {
                hours_early: hours,
                promo_code: appliedPromo?.code || ""
            };
            await axiosInstance.post(
                `/api/v1/users/${userId}/bookings/${bookingId}/booked-rooms/${selectedRoomId}/guest-requests/pre-checkin/early-checkin-request/`,
                passData
            );
            toast.success("Early CheckIn Request Created Successfully");
            navigate("/bookings", { state: { activeRequests: "requests" } });
        } catch (error) {
            toast.error(error?.response?.data?.message || "Some Error Occured");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {(isSubmitting || isLoadingConfig) && <PageLoader />}
            <div className="earlyCheckIn">
                <Header title="Early Check In" para="Pick how early you need the room. The fee is charged per hour."
                    hotelName={hotelName}
                    hotelAddress={hotelAddress}
                    checkInDate={checkInDate}
                    checkOutDate={checkOutDate}
                    status={status}
                    activeTab={activeTab}
                    hotelId={hotelId}
                    bookingId={bookingId}
                    booking_code={booking_code}
                    movetoHotel="movetoHotel"
                />

                <div className="earlyCheckIn-body">

                    <div className="earlyCheckIn-field">
                        <h6>Which room needs the early check-in?</h6>
                        <div className="earlyCheckIn-rooms">
                            {sortedRooms.map((room, index) => {
                                const selectable = isRoomSelectable(room);
                                const selected = room.id === selectedRoomId;
                                const guests = room.guests || [];
                                return (
                                    <div
                                        key={room.id || index}
                                        className={`earlyCheckIn-roomCard${selected ? " is-selected" : ""}${selectable ? "" : " is-blocked"}`}
                                        onClick={() => handleRoomSelect(room)}
                                    >
                                        <div className="earlyCheckIn-roomCard-head">
                                            <span className="earlyCheckIn-roomCard-radio" />
                                            <span className="earlyCheckIn-roomCard-name">
                                                {roomLabel(index)}
                                            </span>
                                            {selectable ? (
                                                <span className="earlyCheckIn-roomCard-count">
                                                    {guests.length} {guests.length === 1 ? "guest" : "guests"}
                                                </span>
                                            ) : (
                                                <span className="earlyCheckIn-roomCard-tag">
                                                    {blockedReason(room)}
                                                </span>
                                            )}
                                        </div>

                                        <div className="earlyCheckIn-roomCard-guests">
                                            {guests.length === 0 ? (
                                                <span className="earlyCheckIn-roomCard-empty">
                                                    No guests added yet
                                                </span>
                                            ) : (
                                                guests.map((guest, guestIndex) => (
                                                    <span className="earlyCheckIn-guestChip" key={guestIndex}>
                                                        <span className="earlyCheckIn-guestChip-avatar">
                                                            {initials(guest.name)}
                                                        </span>
                                                        <span className="earlyCheckIn-guestChip-name">
                                                            {guest.name || "Guest"}
                                                        </span>
                                                        {guest.is_minor && (
                                                            <span className="earlyCheckIn-guestChip-minor">child</span>
                                                        )}
                                                    </span>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {errors.roomId && <span className="error">{errors.roomId}</span>}
                    </div>

                    <div className="earlyCheckIn-field">
                        <h6>How early do you need the room?</h6>
                        <div className="earlyCheckIn-hours">
                            <div className="qtyButton" onClick={decreaseHours}>-</div>
                            <div className="qty">{hours} {hours === 1 ? "hour" : "hours"}</div>
                            <div className="qtyButton" onClick={increaseHours}>+</div>
                        </div>
                        {maxHours !== null && <span className="earlyCheckIn-hint">Up to {maxHours} hours early</span>}
                        {newCheckInTime && <span className="earlyCheckIn-hint">Check in from {newCheckInTime}</span>}
                    </div>

                    <div className="earlyCheckIn-fee">
                        <div className="earlyCheckIn-fee-row">
                            <span>{hours} × ₹{hourlyRate || 0}</span>
                            <span>₹{formatMoney(grossAmount)}</span>
                        </div>
                        {discountAmount > 0 && (
                            <div className="earlyCheckIn-fee-row earlyCheckIn-fee-discount">
                                <span>Discount</span>
                                <span>- ₹{formatMoney(discountAmount)}</span>
                            </div>
                        )}
                        {taxAmount > 0 && (
                            <div className="earlyCheckIn-fee-row">
                                <span>GST ({gstPercentage}%)</span>
                                <span>+ ₹{formatMoney(taxAmount)}</span>
                            </div>
                        )}
                        <div className="earlyCheckIn-fee-row earlyCheckIn-fee-total">
                            <span>Early check-in fee</span>
                            <span>₹{formatMoney(payableAmount)}</span>
                        </div>
                    </div>

                </div>

                {/* Promo Code */}
                <PromoSection
                    userId={userId}
                    hotelId={hotelId}
                    service={EARLY_CHECKIN_TYPE}
                    orderAmount={grossAmount}
                    appliedPromo={appliedPromo}
                    discountAmount={discountAmount}
                    onApply={handleApplyPromo}
                    onRemove={handleRemovePromo}
                    previewDiscount={true}
                />

                <div className='earlyCheckIn-btn'>
                    <button onClick={handleSave} disabled={selectedRoomId === "" || isSubmitting}>
                        Request Early Check-In
                    </button>
                </div>

            </div>
        </>
    )
}
